"use client"

import { useRef, useState } from "react"
import { IconCalendarSearch, IconRefresh } from "@tabler/icons-react"
import { format } from "date-fns"
import { useTranslations } from "next-intl"
import type { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import { NeedsBookingLegend } from "@/components/dashboard/variants/frontdesk/needs-booking-legend"
import { NeedsBookingPrintMenu } from "@/components/dashboard/variants/frontdesk/needs-booking-print-menu"
import {
  BOOKING_FILTERS,
  DAY_RANGES,
  NEED_FILTERS,
  REASON_ICON,
  REASON_LABEL_KEY,
  REASON_TONE,
  parseLocalDate,
  type Need,
  type Reason,
  type useNeedsBooking,
} from "@/components/dashboard/variants/frontdesk/use-needs-booking"

/** The range Select's value for the custom window — never a day count. */
const CUSTOM = "custom"

const toWire = (d: Date) => format(d, "yyyy-MM-dd")

/**
 * The board's filters, sized to sit in the page header's action slot rather
 * than in a filter band of their own.
 *
 * Same Select + icon-button idiom the leads/invoices/appointments indexes use,
 * so it reads as this app's filter row wherever it is mounted; only the
 * placement changed. `size="sm"` because a header cluster sits beside a serif
 * title and the index pages' full-height controls would tower over it.
 *
 * Widths are set for the LONGEST label in either locale — the trigger clamps
 * its value to one line, so anything tighter silently truncates rather than
 * wrapping, and Italian runs a good deal longer than English here
 * ("Prossimi 30 giorni" against "Next 30 days").
 */
export function NeedsBookingFilters({
  board,
  className,
}: {
  board: ReturnType<typeof useNeedsBooking>
  className?: string
}) {
  const t = useTranslations("dashboard")
  const {
    days,
    setDays,
    range,
    setRange,
    rangeLabel,
    reason,
    setReason,
    reasons,
    booking,
    setBooking,
    checkInMode,
    need,
    setNeed,
    needCounts,
    showIdentity,
    counts,
    refresh,
  } = board

  // The custom window's picker (ArtDent 261015). It opens from the range
  // Select's last item and, once a window is active, from the calendar button
  // beside the Select — Radix fires no change when the same item is picked
  // again, so the button is the way back in. `draft` is the picker's own
  // selection: the board is told on Apply, never on a click — the picker's
  // first click already yields a one-day range (from = to), so applying on
  // "both ends set" would close it before the second day could be chosen.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draft, setDraft] = useState<DateRange | undefined>(undefined)
  const anchorRef = useRef<HTMLDivElement>(null)
  const openPicker = () => {
    setDraft(
      range
        ? { from: parseLocalDate(range.from) ?? undefined, to: parseLocalDate(range.to) ?? undefined }
        : undefined,
    )
    // After the Select's own close: it hands focus back to its trigger, and a
    // popover opened in the same tick would read that as "focus outside".
    setTimeout(() => setPickerOpen(true), 0)
  }
  const applyDraft = () => {
    if (!draft?.from) return
    setRange({ from: toWire(draft.from), to: toWire(draft.to ?? draft.from) })
    setPickerOpen(false)
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {/* Why */}
      <Select value={reason} onValueChange={(v) => setReason(v as Reason | "all")}>
        <SelectTrigger size="sm" className="w-[180px]">
          <SelectValue placeholder={t("frontdeskBooking.filter.reasonPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("frontdeskBooking.filter.allReasons")}</SelectItem>
          {reasons.map((key) => {
            const Icon = REASON_ICON[key]
            return (
              <SelectItem key={key} value={key}>
                <span className="flex items-center gap-2">
                  <Icon className={cn("h-3.5 w-3.5", REASON_TONE[key])} aria-hidden />
                  {t(REASON_LABEL_KEY[key])}
                  <span className="tabular-nums text-muted-foreground">
                    {counts.get(key) ?? 0}
                  </span>
                </span>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>

      {/* Status. The logistics desk (Radiance 261001) books nothing, so its
          status is what a trip still owes — the check-in, a ride, or both. */}
      {checkInMode ? (
        <Select value={need} onValueChange={(v) => setNeed(v as Need)}>
          <SelectTrigger size="sm" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* Counts on the three needs only — "All" is the board's own row
                count, already in the table's corner, like "All reasons". */}
            {NEED_FILTERS.filter(({ key }) => key !== "id" || showIdentity).map(({ key, labelKey }) => (
              <SelectItem key={key} value={key}>
                <span className="flex items-center gap-2">
                  {t(labelKey)}
                  {key !== "all" && (
                    <span className="tabular-nums text-muted-foreground">
                      {needCounts.get(key) ?? 0}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Select
          value={booking}
          onValueChange={(v) => setBooking(v as "pending" | "booked" | "all")}
        >
          <SelectTrigger size="sm" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BOOKING_FILTERS.map(({ key, labelKey }) => (
              <SelectItem key={key} value={key}>
                {t(labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Range — server-side, so the counts above stay truthful. The presets
          are a forward horizon; the last item is a custom window, both ends
          (ArtDent 261015: "search by a custom date range"). */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="flex items-center gap-2">
            <Select
              value={range ? CUSTOM : String(days)}
              onValueChange={(v) => (v === CUSTOM ? openPicker() : setDays(Number(v)))}
            >
              <SelectTrigger size="sm" className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_RANGES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {t("frontdeskBooking.range.next", { count: n })}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM}>
                  {rangeLabel ?? t("frontdeskBooking.range.custom")}
                </SelectItem>
              </SelectContent>
            </Select>
            {range && (
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={openPicker}
                aria-label={t("frontdeskBooking.range.edit")}
              >
                <IconCalendarSearch className="h-4 w-4" />
              </Button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="end"
          className="w-auto p-3"
          // The Select and the calendar button live in the anchor, not in the
          // content: a click or a focus on them must not dismiss the picker.
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault()
          }}
        >
          <p className="mb-2 text-xs text-muted-foreground">{t("frontdeskBooking.range.customHint")}</p>
          <Calendar mode="range" numberOfMonths={2} selected={draft} onSelect={setDraft} />
          <div className="mt-2 flex items-center justify-end gap-2">
            {range && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDays(days)
                  setPickerOpen(false)
                }}
              >
                {t("frontdeskBooking.range.clear", { count: days })}
              </Button>
            )}
            <Button size="sm" disabled={!draft?.from} onClick={applyDraft}>
              {t("frontdeskBooking.range.apply")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="outline"
        size="icon"
        className="size-8"
        onClick={refresh}
        aria-label={t("frontdeskBooking.filter.refresh")}
      >
        <IconRefresh className="h-4 w-4" />
      </Button>

      {/* Radiance 261001 — the logistics desk prints its trips as one sheet
          per purpose (Prima / Seconda / Rpt). The clinic coordinator's board
          has no sheet to print. */}
      {checkInMode && <NeedsBookingPrintMenu days={days} />}

      {/* What every icon and colour on a row means — the calendar's legend
          idiom, last in the cluster exactly as it sits in the calendar toolbar. */}
      <NeedsBookingLegend board={board} />
    </div>
  )
}
